/**
 * Knob de volumen y barra de progreso.
 *
 * Knob: rota -135° (mín) a +135° (máx) — 270° totales, con el sprite
 * `r_volumen.png` ya rotado al norte (línea apuntando vertical en reposo).
 * Drag vertical o rueda del ratón = volumen. Progreso: clicable para seek.
 * Actualizado en `timeupdate` del audio.
 */

import { musicAudio } from './audio.js';
import { fmtTime } from './helpers.js';
import { setKnobAngle, setVolume, state } from './state.js';
import { nextTrackAndPlay } from './controls.js';

// ─── Knob ────────────────────────────────────────────────────────────────

/** Aplica el ángulo correspondiente al volumen actual al elemento del knob. */
export function updateKnob(): void {
  const k = document.getElementById('r-knob');
  if (!k) return;
  // Rango clásico -135°..+135° (270° totales). Funciona ahora que el
  // sprite r_volumen.png tiene la línea apuntando vertical en reposo.
  const angle = -135 + state.volume * 270;
  setKnobAngle(angle);
  k.style.transform = `rotate(${angle}deg)`;
}

/** Configura los listeners del knob: drag vertical + wheel + sync inicial. */
export function initKnob(): void {
  const k = document.getElementById('r-knob');
  if (!k) return;

  // Estado de drag local al closure (no en state global)
  let dragActive = false;
  let dragStartY = 0;
  let dragStartVol = 0;

  k.addEventListener(
    'wheel',
    (e: WheelEvent) => {
      if (state.editMode) return;
      e.preventDefault();
      const newVol = Math.max(0, Math.min(1, state.volume - e.deltaY * 0.0015));
      setVolume(newVol);
      musicAudio.volume = newVol;
      updateKnob();
    },
    { passive: false },
  );

  k.addEventListener('mousedown', (e: MouseEvent) => {
    if (state.editMode) return;
    dragActive = true;
    dragStartY = e.clientY;
    dragStartVol = state.volume;
    document.body.style.cursor = 'ns-resize';
    e.preventDefault();
    e.stopPropagation();
  });

  document.addEventListener('mousemove', (e: MouseEvent) => {
    if (!dragActive) return;
    const delta = dragStartY - e.clientY;
    const newVol = Math.max(0, Math.min(1, dragStartVol + delta * 0.005));
    setVolume(newVol);
    musicAudio.volume = newVol;
    updateKnob();
  });

  document.addEventListener('mouseup', () => {
    if (dragActive) {
      dragActive = false;
      document.body.style.cursor = '';
    }
  });

  // Sync inicial del audio con el volumen del estado
  musicAudio.volume = state.volume;
  updateKnob();
}

// ─── Barra de progreso ───────────────────────────────────────────────────

/** Configura el listener de click en la barra de progreso (seek). */
export function initProgress(): void {
  const bar = document.getElementById('r-prog');
  if (!bar) return;
  bar.addEventListener('click', (e: MouseEvent) => {
    if (!musicAudio.duration || state.editMode) return;
    const r = bar.getBoundingClientRect();
    musicAudio.currentTime = ((e.clientX - r.left) / r.width) * musicAudio.duration;
  });
}

/**
 * Configura los listeners del audio principal: timeupdate (actualizar
 * progreso) y ended (siguiente pista). Llamar UNA vez en init.
 */
export function bindAudioListeners(): void {
  musicAudio.addEventListener('timeupdate', () => {
    if (!musicAudio.duration) return;
    const pct = (musicAudio.currentTime / musicAudio.duration) * 100;
    const f = document.getElementById('r-prog-fill');
    const tc = document.getElementById('r-tc');
    const tt = document.getElementById('r-tt');
    if (f) f.style.width = pct + '%';
    if (tc) tc.textContent = fmtTime(musicAudio.currentTime);
    if (tt) tt.textContent = fmtTime(musicAudio.duration);
  });

  // Fin de pista: encadenar la siguiente. `nextTrackAndPlay` ya cubre el
  // caso de "última pista" deteniendo limpiamente. Antes se usaba
  // `selectTrack`, que sólo cargaba la siguiente sin reproducirla y dejaba
  // la radio en pausa entre canciones.
  musicAudio.addEventListener('ended', () => {
    nextTrackAndPlay();
  });
}
